import {PlusOutlined, TeamOutlined, UserOutlined} from '@ant-design/icons';
import {Button, Col, Form, Input, InputNumber, Modal, Row, Select, Tabs, Tag, Typography} from 'antd';
import PropTypes from 'prop-types';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RqAccountantRegister,
  RqAccountantUpdate,
  RqAdminLookupAllRole,
  RqAdminRegisterRole,
  RqAdminUpdateRole,
  RqSupervisorRegister,
  RqSupervisorUpdate,
} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

import CustomerTab from './CustomerTab/CustomerTab';
import {EditEmployeeScreen} from './EditEmployee';
import {EmployeeTab} from './EmployeeTab';

const {Option} = Select;
const {Title}  = Typography;

const TABS_BY_ROLE = {
  ADMIN:      ['employee', 'customer'],
  SUPERVISOR: ['employee'],
  ACCOUNTANT: ['customer'],
};

const ROLE_COLORS = {
  EMPLOYEE:   'blue',
  SUPERVISOR: 'purple',
  ACCOUNTANT: 'cyan',
  ADMIN:      'volcano',
  CUSTOMER:   'green',
  VENDOR:     'gold',
};

const ALL_ROLE_OPTIONS = [
  {label: 'Employee',   value: 'EMPLOYEE'},
  {label: 'Supervisor', value: 'SUPERVISOR'},
  {label: 'Accountant', value: 'ACCOUNTANT'},
  {label: 'Customer',   value: 'CUSTOMER'},
  {label: 'Vendor',     value: 'VENDOR'},
];

const ROLE_BASED_REGISTER_OPTIONS = {
  SUPERVISOR: ['EMPLOYEE'],
  ACCOUNTANT: ['CUSTOMER', 'VENDOR'],
  ADMIN:      ALL_ROLE_OPTIONS.map(r => r.value),
};

const noWhitespace = {
  validator: (_, value) =>
    typeof value === 'string' && value.length > 0 && value.trim() === ''
      ? Promise.reject('Cannot be only whitespace')
      : Promise.resolve(),
};

const SectionLabel = ({children}) => (
  <p className={'text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-1'}>
    {children}
  </p>
);
SectionLabel.propTypes = {children: PropTypes.node.isRequired};

const useRoleBasedMutations = ({loggedInRole, registerCallbacks, updateCallbacks}) => {
  const {mutate: adminRegister,      isLoading: isRegAdmin} = RqAdminRegisterRole(registerCallbacks);
  const {mutate: accountantRegister, isLoading: isRegAcc}   = RqAccountantRegister(registerCallbacks);
  const {mutate: supervisorRegister, isLoading: isRegSup}   = RqSupervisorRegister(registerCallbacks);

  const {mutate: adminUpdate,      isLoading: isUpdAdmin} = RqAdminUpdateRole(updateCallbacks);
  const {mutate: accountantUpdate, isLoading: isUpdAcc}   = RqAccountantUpdate(updateCallbacks);
  const {mutate: supervisorUpdate, isLoading: isUpdSup}   = RqSupervisorUpdate(updateCallbacks);

  const getRegisterMutate = () => {
    switch (loggedInRole) {
      case 'ACCOUNTANT': return accountantRegister;
      case 'SUPERVISOR':  return supervisorRegister;
      default:            return adminRegister;
    }
  };

  const getUpdateMutate = () => {
    switch (loggedInRole) {
      case 'ACCOUNTANT': return accountantUpdate;
      case 'SUPERVISOR':  return supervisorUpdate;
      default:            return adminUpdate;
    }
  };

  return {
    getRegisterMutate,
    getUpdateMutate,
    isRegistering: isRegAdmin || isRegAcc || isRegSup,
    isUpdating:    isUpdAdmin || isUpdAcc || isUpdSup,
  };
};

/* ─── Main Component ─────────────────────────────────────── */

const EmployeeDetails = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editModal, setEditModal] = useState({open: false, employee: null});
  const [form]                    = Form.useForm();

  const auth         = useSelector(selectAuth);
  const token        = auth?.tokenData;
  const loggedInRole = auth?.user?.role?.toUpperCase() ?? 'ADMIN';

  const selectedModalRole = Form.useWatch('role', form);
  const showWages         = selectedModalRole === 'EMPLOYEE';
  const showBusinessInfo  = ['CUSTOMER', 'VENDOR'].includes(selectedModalRole);

  const allowedRegisterRoles = ROLE_BASED_REGISTER_OPTIONS[loggedInRole] ?? ALL_ROLE_OPTIONS.map(r => r.value);
  const modalRoleOptions     = ALL_ROLE_OPTIONS.filter(r => allowedRegisterRoles.includes(r.value));
  const defaultRole          = modalRoleOptions[0]?.value ?? 'EMPLOYEE';

  const {refetch} = RqAdminLookupAllRole('all', token);

  /* ── callbacks ── */
  const registerCallbacks = {
    onSuccess: () => {
      toast.success('Registered successfully! 🎉');
      setModalOpen(false);
      form.resetFields();
      refetch();
    },
    onError: err =>
      toast.error(
        err?.response?.data?.message || err?.data?.message || err?.message || 'Registration failed',
      ),
  };

  const updateCallbacks = {
    onSuccess: () => {
      toast.success('Updated successfully');
      setEditModal({open: false, employee: null});
      refetch();
    },
    onError: err => toast.error(err?.message || 'Failed to update'),
  };

  const {getRegisterMutate, getUpdateMutate, isRegistering, isUpdating} =
    useRoleBasedMutations({loggedInRole, registerCallbacks, updateCallbacks});

  const handleSubmit = () =>
    form.validateFields()
      .then(values => {
        const {role, ...rest} = values;
        getRegisterMutate()({
          role,
          token,
          data: {
            ...rest,
            createdOn:    new Date().toISOString(),
            verifcation:  values.verifcation  ?? false,
            perDaysWages: values.perDaysWages ?? 0,
            wagesRate:    values.wagesRate     ?? 0,
            gstIn:        values.gstIn         ?? '',
            businessName: values.businessName  ?? '',
          },
        });
      })
      .catch(() => {});

  const closeModal = () => {setModalOpen(false); form.resetFields();};

  /* ── role-gated tab items ── */
  const visibleTabs = TABS_BY_ROLE[loggedInRole] ?? TABS_BY_ROLE.ADMIN;

  const allTabItems = [
    {
      key:   'employee',
      label: (
        <span className={'flex items-center gap-2'}>
          <UserOutlined />
          Employees
        </span>
      ),
      children: <EmployeeTab />,
    },
    {
      key:   'customer',
      label: (
        <span className={'flex items-center gap-2'}>
          <TeamOutlined />
          Customers &amp; Vendors
        </span>
      ),
      children: <CustomerTab />,
    },
  ];

  const tabItems     = allTabItems.filter(t => visibleTabs.includes(t.key));
  const defaultTab   = tabItems[0]?.key ?? 'employee';

  return (
    <div className={'min-h-screen md:p-5 p-1'}>
      {/* Header */}

      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <TeamOutlined style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header!mb-0'}>Employee Management</Title>
        </div>

        <Button
          type={'primary'}
          className={'btn-primary'}
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({role: defaultRole});
            setModalOpen(true);
          }}
        >
          Add User
        </Button>
      </div>

      {/* Tabs — role-filtered */}
      <div className={'bg-white rounded-xl shadow-sm'}>
        <Tabs
          defaultActiveKey={defaultTab}
          items={tabItems}
          className={'custom-tabs'}
        />
      </div>

      {/* Register Modal */}
      <Modal
        title={<><UserOutlined className={'mr-2'} />Register New User</>}
        open={modalOpen}
        onCancel={closeModal}
        width={720}
        footer={[
          <Button key={'cancel'} onClick={closeModal}>Cancel</Button>,
          <Button key={'submit'} className={'btn-primary'} loading={isRegistering} onClick={handleSubmit}>
            Register
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout={'vertical'}
          autoComplete={'off'}
          initialValues={{
            email: '', password: '', firstName: '', lastName: '',
            mobile: '', address: '', street: '', apartment: '',
            pinCode: '', city: '', district: '', state: '',
            verifcation: false, role: defaultRole,
            perDaysWages: 0, wagesRate: 0, gstIn: '', businessName: '',
          }}
          onValuesChange={changed => {
            if (changed.role) {
              form.setFieldsValue({perDaysWages: 0, wagesRate: 0, gstIn: '', businessName: '', LDNumber: ''});
            }
          }}
          scrollToFirstError
        >
          <SectionLabel>Personal Information</SectionLabel>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'First Name'} name={'firstName'}
                rules={[
                  {required: true, message: 'First name is required'},
                  noWhitespace,
                  {pattern: /^[A-Za-z\s]+$/, message: 'Only letters allowed'},
                  {min: 2, message: 'Min 2 characters'},
                ]}
              >
                <Input placeholder={'John'} autoComplete={'off'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'Last Name'} name={'lastName'}
                rules={[
                  {required: true, message: 'Last name is required'},
                  noWhitespace,
                  {pattern: /^[A-Za-z\s]+$/, message: 'Only letters allowed'},
                  {min: 2, message: 'Min 2 characters'},
                ]}
              >
                <Input placeholder={'Doe'} autoComplete={'off'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={'Email'} name={'email'}>
                <Input placeholder={'john.doe@example.com'} autoComplete={'new-password'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={'Password'} name={'password'}>
                <Input.Password placeholder={'••••••••'} autoComplete={'new-password'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'Mobile'} name={'mobile'}
                rules={[
                  {required: true, message: 'Mobile number is required'},
                  noWhitespace,
                  {pattern: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit mobile number'},
                ]}
              >
                <Input placeholder={'9876543210'} maxLength={10} autoComplete={'off'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'Role'} name={'role'}
                rules={[{required: true, message: 'Role is required'}]}
              >
                <Select placeholder={'Select role'}>
                  {modalRoleOptions.map(r => (
                    <Option key={r.value} value={r.value}>
                      <Tag color={ROLE_COLORS[r.value]}>{r.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <SectionLabel>Address</SectionLabel>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={'Address'} name={'address'}>
                <Input placeholder={'Full address'} autoComplete={'off'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={'Street'} name={'street'}>
                <Input placeholder={'Street'} autoComplete={'off'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={'Apartment'} name={'apartment'}>
                <Input placeholder={'Apt / Suite / Floor'} autoComplete={'off'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'Pin Code'} name={'pinCode'}
                rules={[{pattern: /^[1-9][0-9]{5}$/, message: 'Enter a valid 6-digit pincode'}]}
              >
                <Input placeholder={'400001'} maxLength={6} autoComplete={'off'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}><Form.Item label={'City'} name={'city'}><Input placeholder={'Mumbai'} autoComplete={'off'} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label={'District'} name={'district'}><Input placeholder={'District'} autoComplete={'off'} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label={'State'} name={'state'}><Input placeholder={'Maharashtra'} autoComplete={'off'} /></Form.Item></Col>
          </Row>

          {showWages && (
            <>
              <SectionLabel>Wages Information</SectionLabel>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label={'Per Day Wages (₹)'} name={'perDaysWages'} rules={[{type: 'number', min: 0, message: 'Must be ≥ 0'}]}>
                    <InputNumber min={0} step={0.01} placeholder={'500'} addonBefore={'₹'} style={{width: '100%'}} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={'Wages Rate (₹)'} name={'wagesRate'} rules={[{type: 'number', min: 0, message: 'Must be ≥ 0'}]}>
                    <InputNumber min={0} step={0.01} placeholder={'60'} addonBefore={'₹'} style={{width: '100%'}} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {showBusinessInfo && (
            <>
              <SectionLabel>Business Information</SectionLabel>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={'Business Name'} name={'businessName'}
                    rules={[
                      {required: true, message: 'Business name is required'},
                      noWhitespace,
                    ]}
                  >
                    <Input placeholder={'Acme Pvt. Ltd.'} autoComplete={'off'} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label={'GST Number (GSTIN)'} name={'gstIn'}
                    rules={[
                      noWhitespace,
                      {pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Enter a valid 15-character GSTIN'},
                    ]}
                  >
                    <Input
                      placeholder={'22AAAAA0000A1Z5'}
                      maxLength={15}
                      autoComplete={'off'}
                      onChange={e => form.setFieldValue('gstIn', e.target.value.toUpperCase())}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label={'LD Number'} name={'LDNumber'} rules={[noWhitespace]}>
                    <Input
                      placeholder={'LD123456'}
                      autoComplete={'off'}
                      onChange={e => form.setFieldValue('LDNumber', e.target.value.toUpperCase())}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* Edit Modal */}
      <EditEmployeeScreen
        open={editModal.open}
        employee={editModal.employee}
        token={token}
        loggedInRole={loggedInRole}
        onSave={getUpdateMutate()}
        isUpdating={isUpdating}
        onClose={() => setEditModal({open: false, employee: null})}
        onSuccess={refetch}
      />
    </div>
  );
};

export default EmployeeDetails;
